import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FiChevronDown,
  FiHeart,
  FiPackage,
  FiShield,
  FiTruck,
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import ProductCard from "./ProductCard";
import QuantitySelector from "./QuantitySelector";
import "../css/productdetails.scss";

const DetailAccordion = ({ title, children, open = false }) => (
  <details className="detail-accordion" open={open}>
    <summary>{title}<FiChevronDown /></summary>
    <div>{children}</div>
  </details>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addCart, toggleWishlist, wishlist } = useContext(CartContext);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    setQuantity(1);
    setSelectedImage(0);
  }, [id]);

  const related = useMemo(() => {
    if (!product) return [];
    return productData
      .filter((item) => item.id !== product.id && item.category === product.category)
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="empty-state-page">
        <PageMeta title="Product not found" />
        <div className="empty-state-card">
          <span className="eyebrow">Off the shelf</span>
          <h1>We can’t find this product.</h1>
          <p>It may have sold out or moved with the monthly selection.</p>
          <Link className="button button--primary" to="/shop">Back to the shop</Link>
        </div>
      </main>
    );
  }

  const isSaved = wishlist.includes(product.id);
  const buyNow = () => {
    addCart(product.id, quantity, product.unit);
    navigate("/cartpage");
  };

  return (
    <main className="product-detail-page">
      <PageMeta title={product.name} description={product.description} />
      <div className="container breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link><span>/</span>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link><span>/</span>
        <span>{product.name}</span>
      </div>

      <section className="container product-detail-layout">
        <div className="product-gallery">
          <div className="product-gallery__thumbs">
            {product.images.map((image, index) => (
              <button
                type="button"
                key={image}
                className={selectedImage === index ? "is-active" : ""}
                onClick={() => setSelectedImage(index)}
                aria-label={`Show product image ${index + 1}`}
              >
                <img src={image} alt="" />
              </button>
            ))}
          </div>
          <div className="product-gallery__main">
            <img src={product.images[selectedImage]} alt={product.name} />
            {product.newArrival && <span className="badge">New arrival</span>}
          </div>
        </div>

        <aside className="product-panel">
          <p className="eyebrow">{product.subcategory} · {product.attributes.origin}</p>
          <h1>{product.name}</h1>
          <div className="product-panel__rating" aria-label={`${product.rating} out of 5 stars`}>
            <span>★★★★★</span>
            <a href="#product-details">{product.rating} · {product.reviews} reviews</a>
          </div>
          <div className="product-panel__price">
            {product.compareAtPrice && <del>{formatPrice(product.compareAtPrice)}</del>}
            <strong>{formatPrice(product.price)}</strong>
            <span>/ {product.unit}</span>
          </div>
          <p className="product-panel__description">{product.description}</p>

          <div className="product-option">
            <div><span>Pack size</span><small>{product.stock} in stock</small></div>
            <button type="button" className="pack-option is-active">{product.unit}</button>
          </div>

          <div className="product-buy-row">
            <QuantitySelector value={quantity} onChange={setQuantity} />
            <button className="button button--primary product-add" type="button" onClick={() => addCart(product.id, quantity, product.unit)}>
              Add to bag · {formatPrice(product.price * quantity)}
            </button>
            <button
              type="button"
              className={`product-save ${isSaved ? "is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={isSaved ? "Remove from favourites" : "Save to favourites"}
              aria-pressed={isSaved}
            >
              <FiHeart />
            </button>
          </div>
          <button className="button button--outline buy-now" type="button" onClick={buyNow}>Buy now</button>

          <div className="product-promises">
            <div><FiTruck /><span><b>Next-day chilled delivery</b><small>Order before {siteConfig.delivery.orderCutoff}</small></span></div>
            <div><FiPackage /><span><b>Free over {formatPrice(siteConfig.delivery.freeThreshold)}</b><small>Otherwise {formatPrice(siteConfig.delivery.standardFee)}</small></span></div>
            <div><FiShield /><span><b>Quality guarantee</b><small>Tell us within 24 hours if something is wrong</small></span></div>
          </div>

          <div id="product-details" className="product-accordions">
            <DetailAccordion title="Tasting notes & details" open>
              <dl>
                {Object.entries(product.attributes).map(([key, value]) => (
                  <React.Fragment key={key}>
                    <dt>{key}</dt><dd>{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </DetailAccordion>
            <DetailAccordion title="Freshness & storage">
              <p>Best before {product.bestBefore}. {product.attributes.storage}. Bring cheese to room temperature for 30–45 minutes before serving.</p>
            </DetailAccordion>
            <DetailAccordion title="Delivery & returns">
              <p>Delivered in temperature-conscious packaging. Fresh food is covered by our quality guarantee; see the full policy for details.</p>
              <Link className="text-link" to="/shipping">Read delivery guide</Link>
            </DetailAccordion>
          </div>
        </aside>
      </section>

      <section className="related-section section-space">
        <div className="container">
          <div className="section-heading"><div><p className="eyebrow">Keep tasting</p><h2>You may also like</h2></div></div>
          <div className="product-grid product-grid--four">
            {related.map((item) => <ProductCard key={item.id} product={item} />)}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
